import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useNavigate, useLocation } from 'react-router';
import { useButton } from 'react-aria';

import SearchField from 'client/components/aria/SearchField/SearchField';
import Button from 'client/components/aria/Button/Button';
import ShowResults from 'client/components/aria/ShowResults/ShowResults';

import defaultAriaProps from 'client/utils/constants/ariaPropTypes';
import styles from './search.module.scss';
import PropTypes from 'prop-types';

const Search = (props) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const { totalElements, size } = props;
    const [search, setSearch] = useState('');
    const [searchParams, setSearchParams] = useState(null);

    const inputRef = useRef();
    const buttonRef = useRef();

    useEffect(() => {
        const params = new URLSearchParams(location?.search);
        setSearchParams(params.get('name'));
    }, [location]);

    const handleClearSearch = () => {
        const params = new URLSearchParams(location?.search);
        params.delete('name');
        params.delete('page');
        navigate(location?.pathname + '?' + params.toString());
        setSearch('');
    };

    const { buttonProps } = useButton({
        onPress: handleClearSearch,
        elementType: 'span'
    }, buttonRef);

    const handleSearch = (search) => {
        const params = new URLSearchParams(location?.search);
        if (search && params.get('name') !== search) {
            params.set('name', search);
            params.delete('page');
            navigate(location?.pathname + '?' + params.toString());
        } else if (!search && params.get('name')) { // If text box is clear, but is a previous search value then clear box
            handleClearSearch();
        }
    };

    return (
        <div className={['govuk-grid-column-one-half', styles.searchSection].join(' ')}>
            <div data-testid={props['data-testid'] ? props['data-testid'] + '.search' : null} className={styles.search}>
                <SearchField
                    ref={inputRef}
                    aria-labelledby="searchButton"
                    placeholder={intl.formatMessage({ id: 'ariaSearch.placeholder' })}
                    value={search}
                    onChange={setSearch}
                    className={styles.input}
                    onSubmit={() => handleSearch(search)}
                    data-testid={props['data-testid'] ? props['data-testid'] + '.search.input' : null}
                />
                <Button
                    id='searchButton'
                    data-testid={props['data-testid'] ? props['data-testid'] + '.search.button' : null}
                    onPress={() => handleSearch(search)}
                >
                    {intl.formatMessage({ id: 'ariaSearch.button' })}
                </Button>
            </div>
            {searchParams
                ? <>
                    <ShowResults
                        data-testid={props['data-testid'] ? props['data-testid'] + '.search.results' : null}
                        totalElements={totalElements}
                        size={size}
                    />
                    &nbsp;
                    {searchParams ? `for "${searchParams}" | ` : null}
                    <span
                        className={['govuk-link', styles.link].join(' ')}
                        {...buttonProps}
                        ref={buttonRef}
                        data-testid={props['data-testid'] ? props['data-testid'] + '.search.clear' : null}
                    >
                        {intl.formatMessage({ id: 'ariaSearch.clear' })}
                    </span>
                </>
                : null
            }
        </div>
    );
};

Search.propTypes = {
    ...defaultAriaProps,
    totalElements: PropTypes.number.isRequired,
    size: PropTypes.number.isRequired
};

export default Search;
